import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { RING_BUDGET_SECONDS } from "./routingApi";
import { PALETTE } from "../callsPalette";

/**
 * Visualises the 45-second window a staged strategy has: one segment per agent that will actually
 * get a turn, then either an "everyone rings" tail or nothing. Turns an abstract "seconds per
 * agent" number into something an admin can sanity-check — and surfaces the one real constraint
 * (Meta ends an unanswered call after ~30-60s) before it bites in production.
 */
export default function RingBudgetBar({ agentCount, secondsPerAgent, whenNobodyAnswers }) {
  const turns = Math.max(0, Math.min(agentCount, Math.floor(RING_BUDGET_SECONDS / secondsPerAgent)));
  const used = turns * secondsPerAgent;
  const tail = Math.max(0, RING_BUDGET_SECONDS - used);
  const overflow = agentCount > turns;
  const pct = (s) => `${(s / RING_BUDGET_SECONDS) * 100}%`;

  const summary =
    turns === 0
      ? "No agents to ring yet."
      : `${turns} agent${turns === 1 ? "" : "s"} get${turns === 1 ? "s" : ""} ${secondsPerAgent}s each${
          whenNobodyAnswers === "ring_everyone" ? ", then everyone rings together." : ", then the call goes unanswered."
        }`;

  return (
    <Box>
      <Box
        role="img"
        aria-label={`Ring timeline over ${RING_BUDGET_SECONDS} seconds. ${summary}`}
        sx={{ display: "flex", height: 28, borderRadius: 1.5, overflow: "hidden", border: (t) => `1px solid ${t.palette.divider}`, bgcolor: "action.hover" }}
      >
        {Array.from({ length: turns }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: pct(secondsPerAgent),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11.5,
              fontWeight: 700,
              color: "#fff",
              bgcolor: PALETTE.blue.fill,
              opacity: 1 - i * 0.12,
              borderRight: i < turns - 1 ? "1px solid rgba(255,255,255,.5)" : "none",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {secondsPerAgent >= 8 ? `Agent ${i + 1}` : i + 1}
          </Box>
        ))}
        {tail > 0 && whenNobodyAnswers === "ring_everyone" && turns > 0 && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11.5,
              fontWeight: 600,
              color: "text.secondary",
              backgroundImage: `repeating-linear-gradient(45deg, ${alpha(PALETTE.blue.ring, 0.18)} 0 6px, transparent 6px 12px)`,
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {tail >= 8 ? "Everyone" : ""}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>0s</Typography>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{RING_BUDGET_SECONDS}s · WhatsApp ends unanswered calls at ~30–60s</Typography>
      </Box>
      <Typography sx={{ fontSize: 13, mt: 1 }}>{summary}</Typography>
      {overflow && (
        <Typography sx={{ fontSize: 12.5, mt: 0.5, color: PALETTE.serious.fill }}>
          {agentCount} agents × {secondsPerAgent}s is more than the {RING_BUDGET_SECONDS}s window — only the first {turns} get their own turn.
          {whenNobodyAnswers === "ring_everyone" ? " The rest are still covered by the final ring-everyone step." : " Lower the seconds per agent so everyone gets a turn."}
        </Typography>
      )}
    </Box>
  );
}
